/**
 * =============================================================================
 * PUBLIC AVAILABILITY CARD (Phase 4.5 controlled-launch slice)
 * =============================================================================
 *
 * Minimal tenant-facing control for the controlled public-availability settings
 * — NOT a broad settings overhaul. Shows whether the public widget/page is
 * Hidden / Coming Soon / Visible and whether customers can currently submit
 * questions, and lets the admin change the public-facing settings:
 *   - `publicLaunchMode` (Hidden / Coming Soon / Visible), and
 *   - `submissionAcknowledgementEnabled` (opt-in submission-acknowledgement email;
 *     only takes effect while Visible).
 *
 * It reads/writes these via the existing admin settings endpoints
 * (`GET`/`PUT /api/admin/settings`); all other settings keys are preserved
 * server-side. On save the card sends ONLY the dirty fields (an
 * acknowledgement-only change does not re-send `publicLaunchMode`), while keeping
 * the explicit confirmation step for transitions INTO Visible.
 *
 * ADR-025 B-2: (a) the optional `onDirtyChange` reports unsaved-edit
 * transitions to the shell (§3.1.4 — `BL-ADMIN-M1-EXPIRY-DIRTY-GUARD-001`;
 * absent under the legacy layout → behaviour unchanged); (b) the VISIBLE
 * confirm dialog rides the A-0 `AccessibleModal` (M11 hard-pass — this file
 * changed in B-2, pulling its dialog in per ext-UX-09).
 * =============================================================================
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, FlexItem, Panel, Select, Switch, Text } from '@bigcommerce/big-design';
import {
  PUBLIC_LAUNCH_MODES,
  PUBLIC_LAUNCH_LABELS,
  resolvePublicLaunchMode,
  type PublicLaunchMode,
  type StoreSettingsConflict,
  type StoreSettingsExpectedState,
  type StoreSettingsPatch,
  type StoreSettingsWriteField,
} from '@find-my-part/types';
import { useSession } from '../../hooks/useSession';
import { useToast } from '../../hooks/useToast';
import { useTenantSettings } from '../../providers/TenantSettingsProvider';
import { AccessibleModal } from '../common/AccessibleModal';
import { StatusChip } from '../common/StatusChip';
import {
  isAmbiguousSettingsFailure,
  readSettingsConflict,
  selectUsableSettingsExpectedState,
  SettingsConflictNotice,
} from './SettingsConflictNotice';

/** Map a launch mode to a BigDesign badge variant. */
const MODE_BADGE_VARIANT: Record<PublicLaunchMode, 'success' | 'warning' | 'secondary'> = {
  VISIBLE: 'success',
  COMING_SOON: 'warning',
  HIDDEN: 'secondary',
};

const SELECT_OPTIONS = PUBLIC_LAUNCH_MODES.map((mode) => ({
  value: mode,
  content: PUBLIC_LAUNCH_LABELS[mode],
}));

export interface LaunchModeCardProps {
  /**
   * ADR-025 B-2 (§3.1.4 — `BL-ADMIN-M1-EXPIRY-DIRTY-GUARD-001`): unsaved-edit
   * reporting to the shell. Fired on TRANSITIONS only (clean→dirty /
   * dirty→clean), never per keystroke/re-render. "Dirty" = the selected
   * values differ from the last-loaded/saved values (the card's existing
   * selected-vs-current state). OPTIONAL: the flag-OFF legacy layout passes
   * nothing and the card behaves exactly as before (rollback surface, M9).
   */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Admin card for viewing and changing the tenant public launch mode.
 */
export function LaunchModeCard({
  onDirtyChange,
}: LaunchModeCardProps = {}): React.ReactElement | null {
  const { token, isAuthenticated } = useSession();
  const { showSuccess, showError } = useToast();
  const tenantSettings = useTenantSettings();

  const [currentMode, setCurrentMode] = useState<PublicLaunchMode | null>(null);
  const [selectedMode, setSelectedMode] = useState<PublicLaunchMode | null>(null);
  // Opt-in submission-acknowledgement email (no effect unless mode is VISIBLE).
  const [currentAck, setCurrentAck] = useState(false);
  const [selectedAck, setSelectedAck] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Open only while confirming a transition INTO Visible (public exposure).
  const [confirmVisibleOpen, setConfirmVisibleOpen] = useState(false);
  const [confirmExpectedOverride, setConfirmExpectedOverride] = useState<
    Partial<StoreSettingsExpectedState> | undefined
  >(undefined);
  const [confirmPayloadOverride, setConfirmPayloadOverride] = useState<
    StoreSettingsPatch | undefined
  >(undefined);
  const [conflict, setConflict] = useState<StoreSettingsConflict | null>(null);
  const restoreConflictFocusPendingRef = useRef(false);

  const restoreConflictFocus = useCallback(() => {
    const save = document.querySelector<HTMLButtonElement>('[data-testid="launch-mode-save"]');
    if (save && !save.disabled) {
      save.focus();
      return;
    }
    document.querySelector<HTMLElement>('[data-testid="launch-mode-select"]')?.focus();
  }, []);

  // Restore only after React has committed the conflict notice removal. Doing
  // this from the click handler's microtask can focus a node that React then
  // replaces, leaving focus on <body> when the notice unmounts.
  useEffect(() => {
    if (conflict !== null || !restoreConflictFocusPendingRef.current) return;
    restoreConflictFocusPendingRef.current = false;
    restoreConflictFocus();
  }, [conflict, restoreConflictFocus]);

  const handleSave = useCallback(
    async (
      expectedOverride?: Partial<StoreSettingsExpectedState>,
      payloadOverride?: StoreSettingsPatch
    ): Promise<boolean> => {
    if (!token || !selectedMode || !tenantSettings) return false;
    const modeChanged = selectedMode !== currentMode;
    const ackChanged = selectedAck !== currentAck;

    // Send only the dirty fields so an acknowledgement-only save does not
    // re-send (and the server does not re-write) the unchanged launch mode.
    const derivedPayload: {
      publicLaunchMode?: PublicLaunchMode;
      submissionAcknowledgementEnabled?: boolean;
    } = {};
    if (modeChanged) derivedPayload.publicLaunchMode = selectedMode;
    if (ackChanged) derivedPayload.submissionAcknowledgementEnabled = selectedAck;
    const payload = payloadOverride ?? derivedPayload;
    if (Object.keys(payload).length === 0) return false;

    setSaving(true);
    try {
      const res = await tenantSettings.update(payload, expectedOverride);
      if (res.success) {
        setCurrentMode(res.data.publicLaunchMode);
        setSelectedMode(res.data.publicLaunchMode);
        setCurrentAck(res.data.submissionAcknowledgementEnabled);
        setSelectedAck(res.data.submissionAcknowledgementEnabled);
        setConflict(null);
        showSuccess('Settings saved.');
        return true;
        } else {
          const stale = readSettingsConflict(res.error);
          if (stale) {
            if (stale.recovery === 'direct') {
              tenantSettings.rebaseExpected(stale.observed);
              if (Object.prototype.hasOwnProperty.call(stale.observed, 'publicLaunchMode')) {
                setCurrentMode(resolvePublicLaunchMode(stale.observed.publicLaunchMode));
          }
          if (
            Object.prototype.hasOwnProperty.call(
              stale.observed,
              'submissionAcknowledgementEnabled'
            )
              ) {
                setCurrentAck(stale.observed.submissionAcknowledgementEnabled === true);
              }
            }
            setConflict(stale);
          } else if (isAmbiguousSettingsFailure(res.error)) {
            showError('Your change may have been saved — refresh to confirm.');
        } else {
          showError(res.error.message ?? 'Failed to update settings');
        }
        return false;
      }
    } catch {
      showError('Your change may have been saved — refresh to confirm.');
      return false;
    } finally {
      setSaving(false);
      setConfirmVisibleOpen(false);
        setConfirmExpectedOverride(undefined);
        setConfirmPayloadOverride(undefined);
      }
    },
    [
      token,
      selectedMode,
      currentMode,
      selectedAck,
      currentAck,
      showSuccess,
      showError,
      tenantSettings,
    ]
  );

  // Going public is the one transition with customer-visible consequences, so
  // it gets an explicit confirmation step. Every other transition only reduces
  // exposure and can save immediately.
  const isGoingVisible = selectedMode === 'VISIBLE' && currentMode !== 'VISIBLE';

  const handleConflictReapply = useCallback(async () => {
    if (!conflict || selectedMode === null || !tenantSettings) return;
    let baselineMode = currentMode;
    let baselineAck = currentAck;
    let expectedOverride: Partial<StoreSettingsExpectedState> = conflict.observed;
    let rawBaseline: Partial<StoreSettingsExpectedState> = conflict.observed;

    if (conflict.recovery === 'refresh_required') {
      setSaving(true);
      const refreshed = await tenantSettings.refresh();
      if (!refreshed.success) {
        setSaving(false);
        showError('Could not refresh current settings. Your draft is preserved; try again.');
        return;
      }
      baselineMode = refreshed.data.publicLaunchMode;
      baselineAck = refreshed.data.submissionAcknowledgementEnabled;
      setCurrentMode(baselineMode);
      setCurrentAck(baselineAck);
      expectedOverride = refreshed.data.expectedState ?? {};
      rawBaseline = expectedOverride;
    }

    const recovery: StoreSettingsPatch = {};
    if (selectedMode !== baselineMode) recovery.publicLaunchMode = selectedMode;
    if (selectedAck !== baselineAck) {
      recovery.submissionAcknowledgementEnabled = selectedAck;
    }
    // Original attempted fields use raw-state convergence, not resolver-
    // effective convergence. An absent key and an explicitly stored default
    // are distinct CAS states even when the form renders them identically.
    if (Object.prototype.hasOwnProperty.call(conflict.expected, 'publicLaunchMode')) {
      if (JSON.stringify(rawBaseline.publicLaunchMode) === JSON.stringify(selectedMode)) {
        delete recovery.publicLaunchMode;
      } else {
        recovery.publicLaunchMode = selectedMode;
      }
    }
    if (
      Object.prototype.hasOwnProperty.call(conflict.expected, 'submissionAcknowledgementEnabled')
    ) {
      if (
        JSON.stringify(rawBaseline.submissionAcknowledgementEnabled) === JSON.stringify(selectedAck)
      ) {
        delete recovery.submissionAcknowledgementEnabled;
      } else {
        recovery.submissionAcknowledgementEnabled = selectedAck;
      }
    }
    if (Object.keys(recovery).length === 0) {
      setSaving(false);
      restoreConflictFocusPendingRef.current = true;
      setConflict(null);
      return;
    }
    const recoveryFields = Object.keys(recovery) as StoreSettingsWriteField[];
    // Direct conflicts are already strictly decoded and the provider merges
    // this partial observation onto its complete token. The refresh-required
    // lane must instead prove every outgoing raw predicate is present and
    // bounded before any repair PUT is attempted.
    const usableExpected =
      conflict.recovery === 'direct'
        ? expectedOverride
        : selectUsableSettingsExpectedState(
            expectedOverride as StoreSettingsExpectedState,
            recoveryFields
          );
    if (!usableExpected) {
      setSaving(false);
      showError(
        'Current settings were refreshed, but a stored value needs operator repair before this draft can be reapplied.'
      );
      return;
    }
    restoreConflictFocusPendingRef.current = true;
    if (recovery.publicLaunchMode === 'VISIBLE' && baselineMode !== 'VISIBLE') {
      setSaving(false);
      setConfirmExpectedOverride(usableExpected);
      setConfirmPayloadOverride(recovery);
      setConfirmVisibleOpen(true);
      return;
    }
    const saved = await handleSave(usableExpected, recovery);
    if (!saved) restoreConflictFocusPendingRef.current = false;
  }, [
    conflict,
    currentAck,
    currentMode,
    handleSave,
    selectedAck,
    selectedMode,
    showError,
    tenantSettings,
  ]);

  const dismissConflict = useCallback(() => {
    restoreConflictFocusPendingRef.current = true;
    setConflict(null);
  }, []);

  const handleSaveClick = useCallback(() => {
    if (isGoingVisible) {
      setConfirmExpectedOverride(undefined);
      setConfirmPayloadOverride(undefined);
      setConfirmVisibleOpen(true);
      return;
    }
    void handleSave();
  }, [isGoingVisible, handleSave]);

  // Unsaved-edit presence (ADR-025 B-2 / §3.1.4): the selected values differ
  // from the last-loaded/saved snapshot. Null selectedMode (loading, load
  // error, pre-auth) is never dirty. Same expression the Save gate always
  // used — hoisted above the auth guard so the reporting hooks below run
  // unconditionally (hook order).
  const dirty =
    selectedMode !== null && (selectedMode !== currentMode || selectedAck !== currentAck);

  const conflictNoticeDraft: StoreSettingsPatch = {
    ...(selectedMode !== null ? { publicLaunchMode: selectedMode } : {}),
    submissionAcknowledgementEnabled: selectedAck,
  };

  useEffect(() => {
    if (!tenantSettings) return;
    setLoading(tenantSettings.status === 'loading');
    setLoadError(tenantSettings.status === 'error');
    if (tenantSettings.status !== 'ready' || !tenantSettings.settings || dirty) return;
    setCurrentMode(tenantSettings.settings.publicLaunchMode);
    setSelectedMode(tenantSettings.settings.publicLaunchMode);
    setCurrentAck(tenantSettings.settings.submissionAcknowledgementEnabled);
    setSelectedAck(tenantSettings.settings.submissionAcknowledgementEnabled);
  }, [dirty, tenantSettings]);
  // Report dirty TRANSITIONS to the optional shell listener (never per
  // re-render — the ref gate fires only on a value change). Without the prop
  // this effect is behaviourally inert, so the legacy layout (which passes
  // nothing) renders this card unchanged. The callback is read through a ref
  // so a new function identity per parent render never re-fires the effect.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);
  const lastReportedDirty = useRef(false);
  useEffect(() => {
    if (lastReportedDirty.current === dirty) return;
    lastReportedDirty.current = dirty;
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);
  // A card that is gone is not dirty. Defensive: the shell keeps Settings
  // keep-alive-mounted, so this fires only if a future layout unmounts a
  // dirty card — the indicator must not stick.
  useEffect(
    () => () => {
      if (lastReportedDirty.current) onDirtyChangeRef.current?.(false);
    },
    []
  );

  // Not authenticated — nothing to control (parent handles the login UX).
  if (!isAuthenticated || !token) {
    return null;
  }

  const submissionsEnabled = currentMode === 'VISIBLE';

  return (
    <Panel header="Public availability" data-testid="launch-mode-card">
      <SettingsConflictNotice
        conflict={conflict}
        draft={conflictNoticeDraft}
        labels={{
          publicLaunchMode: 'Public launch mode',
          submissionAcknowledgementEnabled: 'Submission confirmation email',
        }}
        formatValue={(field: StoreSettingsWriteField, value: unknown) => {
          if (field === 'publicLaunchMode' && typeof value === 'string') {
            return PUBLIC_LAUNCH_LABELS[value as PublicLaunchMode] ?? value;
          }
          if (typeof value === 'boolean') return value ? 'Enabled' : 'Disabled';
          return value === null || value === undefined ? 'Not explicitly set' : String(value);
        }}
        busy={saving}
        onReapply={() => {
          void handleConflictReapply();
        }}
        onDismiss={dismissConflict}
      />
      {loading ? (
        <Text>Loading…</Text>
      ) : loadError ? (
        <Text color="danger">Could not load the current public availability setting.</Text>
      ) : (
        <>
          <Flex alignItems="center" flexGap="8px" marginBottom="small">
            <FlexItem>
              <Text bold marginBottom="none">
                Current status:
              </Text>
            </FlexItem>
            <FlexItem>
              {currentMode && (
                <StatusChip
                  label={PUBLIC_LAUNCH_LABELS[currentMode]}
                  variant={MODE_BADGE_VARIANT[currentMode]}
                />
              )}
            </FlexItem>
          </Flex>

          <Text marginBottom="small" data-testid="launch-mode-submission-state">
            {submissionsEnabled
              ? 'Customers can currently submit questions on your storefront.'
              : 'Customers cannot currently submit questions — the public widget is suppressed.'}
          </Text>

          {submissionsEnabled && (
            <Box
              backgroundColor="warning10"
              borderRadius="normal"
              padding="small"
              marginBottom="medium"
              data-testid="launch-mode-visible-banner"
            >
              <Text bold marginBottom="none" color="warning">
                Public submissions are live.
              </Text>
              <Text marginBottom="none" color="secondary">
                Your storefront is <strong>Visible</strong> — any customer can submit questions
                right now. Switch back to <strong>Hidden</strong> or <strong>Coming Soon</strong> to
                stop accepting submissions.
              </Text>
            </Box>
          )}

          <Box marginBottom="medium">
            <Text color="secondary">
              Hiding the “Find My Part” link in your BigCommerce navigation only affects
              discoverability — the page still works if someone has the direct link. This setting is
              the actual control: when it is not <strong>Visible</strong>, customers see a “coming
              soon / unavailable” message and cannot submit questions, even via the direct URL.
            </Text>
          </Box>

          <Box marginBottom="medium" data-testid="submission-ack-section">
            <Flex alignItems="center" flexGap="12px" marginBottom="xxSmall">
              <FlexItem flexGrow={0}>
                <Switch
                  checked={selectedAck}
                  disabled={saving}
                  onChange={(e) => setSelectedAck(e.target.checked)}
                  data-testid="submission-ack-toggle"
                />
              </FlexItem>
              <FlexItem flexGrow={1}>
                <Text bold marginBottom="none">
                  Email a confirmation when a customer submits a question
                </Text>
              </FlexItem>
            </Flex>
            <Text color="secondary" marginBottom="none">
              When enabled, each customer receives a single “we received your question, it’s under
              review” email right after they submit. This only takes effect while public submissions
              are <strong>Visible</strong>; it has no effect while Hidden or Coming Soon. Recipients
              can still unsubscribe.
            </Text>
          </Box>

          <Flex alignItems="flex-end" flexGap="12px">
            <FlexItem flexGrow={0}>
              <Select
                label="Public launch mode"
                options={SELECT_OPTIONS}
                value={selectedMode ?? undefined}
                disabled={saving}
                onOptionChange={(value: PublicLaunchMode) => setSelectedMode(value)}
                data-testid="launch-mode-select"
              />
            </FlexItem>
            <FlexItem flexGrow={0}>
              <Button
                onClick={handleSaveClick}
                disabled={!dirty || saving}
                isLoading={saving}
                data-testid="launch-mode-save"
              >
                Save
              </Button>
            </FlexItem>
          </Flex>

          {/* ADR-025 B-2 — M11 retrofit (ext-UX-09 file-scope rule: B-2
              modifies this file, so this dialog enters the hard-pass): the
              VISIBLE confirm now rides the A-0 `AccessibleModal`, KEPT
              MOUNTED (isOpen-toggled — the old `{open && <Modal>}` pattern
              unmounted it in the same commit as close, so A-0's open→closed
              focus restore could never fire). Confirm-gating logic (only on
              transitions INTO Visible), handlers, and disabled states are
              behaviour-preserved. */}
          <AccessibleModal
            isOpen={confirmVisibleOpen}
            // B-6 resolves B2-R1-2 (final-decisions Q3): while the VISIBLE save
            // is in flight, NO dismissal route closes the confirm — onClose
            // covers the header X (invoked unconditionally by big-design), Esc
            // and backdrop ride the same predicate (the landed A-Q1 pattern).
            onClose={
              saving
                ? () => undefined
                : () => {
                    setConfirmVisibleOpen(false);
                    setConfirmExpectedOverride(undefined);
                    setConfirmPayloadOverride(undefined);
                  }
            }
            closeOnEscKey={!saving}
            closeOnClickOutside={!saving}
            header="Make Find My Part visible to customers?"
            restoreFocusTo={() => {
              // Re-query the CURRENT DOM at restore time — big-design
              // re-creates nodes on re-render; never hold a node ref (the
              // documented focus bug class). Cancel/Esc leaves the card dirty
              // → the Save trigger is enabled and takes focus back. After a
              // CONFIRMED save the card is clean → Save is rendered-but-
              // DISABLED (focus() would silently no-op), so restore lands on
              // the always-enabled mode Select instead.
              const save = document.querySelector<HTMLButtonElement>(
                '[data-testid="launch-mode-save"]'
              );
              if (save && !save.disabled) return save;
              const select = document.querySelector<HTMLElement>(
                '[data-testid="launch-mode-select"]'
              );
              if (!select) return null;
              // Return the first focusable node of the Select. big-design@1.8
              // renders the combobox as an inner <input>; the broadened query
              // (role/tabindex-aware, ext B2-F1) also catches a `[role=
              // "combobox"]`/`[tabindex]` host if a future big-design changes
              // the internals, so restore degrades to a focusable node rather
              // than null→<body>. The confirmed-path test pins the live target.
              const FOCUSABLE = 'input, button, select, textarea, [tabindex], [role="combobox"]';
              return select.matches(FOCUSABLE)
                ? select
                : select.querySelector<HTMLElement>(FOCUSABLE);
            }}
            actions={[
              {
                text: 'Cancel',
                variant: 'secondary' as const,
                onClick: () => {
                  setConfirmVisibleOpen(false);
                  setConfirmExpectedOverride(undefined);
                  setConfirmPayloadOverride(undefined);
                },
                disabled: saving,
              },
              {
                text: 'Yes, make it Visible',
                variant: 'primary' as const,
                onClick: () => {
                  void handleSave(confirmExpectedOverride, confirmPayloadOverride);
                },
                disabled: saving,
                isLoading: saving,
              },
            ]}
            data-testid="launch-mode-confirm-modal"
          >
            <Text>
              Switching to <strong>Visible</strong> opens your storefront to the public: customers
              will be able to <strong>submit questions immediately</strong>.
            </Text>
            <Text>
              Make sure your content and notification settings are ready. You can switch back to
              Hidden or Coming Soon at any time to stop accepting submissions.
            </Text>
          </AccessibleModal>
        </>
      )}
    </Panel>
  );
}
